
import React, { useState, useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import { SunsetAPIAccountContext } from "../../Networking/Interface/Account/AccountAPIProvider";
import { ToastType } from "../../Components/Toast/Toast";
import { useToast } from "../../Components/Toast/ToastContext";
import { Button, Input } from "antd";

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const InputBox = styled(Input)`
  display: flex;
  align-items: center;
  margin-left: 15px;
  width: 90px;
  margin-right: 1px;
`;

interface SetAdminLevelButtonProps {
  userId: number;
  onSuccess?: () => void; // Ensure onSuccess prop is provided
}

const SetAdminLevelButton: React.FC<SetAdminLevelButtonProps> = ({ userId, onSuccess }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [adminLevel, setAdminLevel] = useState<string>("");
  const { showToast } = useToast();
  const api = useContext(SunsetAPIAccountContext);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsEditing(false);
        setAdminLevel(""); // Clear the input field
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async () => {
    const level = parseInt(adminLevel, 10);

    if (isNaN(level) || level < 0 || level > 9) {
      showToast(ToastType.Error, "Invalid admin level", 5);
      return;
    }

    try {
      const response = await api?.setAdminLevel(userId, level);
      if (!response?.success) {
        throw new Error("Failed to set admin level");
      }
      showToast(ToastType.Success, "Admin level set successfully", 5);
      setIsEditing(false); // Close the input field after successful submission
      setAdminLevel(""); // Clear the input field
      if (onSuccess) {
        onSuccess(); // Call onSuccess if provided
      }
    } catch (error) {
      showToast(ToastType.Error, "Failed to set admin level", 5);
    }
  };

  return (
    <Container ref={containerRef}>
      {isEditing ? (
        <>
          <InputBox
            placeholder="Admin Level"
            value={adminLevel}
            onChange={(e) => setAdminLevel(e.target.value)}
          />
          <Button style={{ border: "none" }} onClick={handleSubmit}>
            Set
          </Button>
        </>
      ) : (
        <Button style={{ border: "none" }} onClick={() => setIsEditing(true)}>Set Admin Level</Button>
      )}
    </Container>
  );
};

export default SetAdminLevelButton;
